import { FormikErrors, useFormik } from 'formik'
import queryString from 'query-string'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Alert, Button, Card, CardBody, Col, Container, Form, FormFeedback, Input, Label, Row, Spinner } from 'reactstrap'
import * as Yup from 'yup'
import auth from '../../api/auth/hooks'
import logoLight from '../../assets/images/dimedia-crm-logo-white.png'
import { PASSWORD_REGEX } from '../../common/constants'
import AuthLayout from './AuthLayout'
import ObscurePassword from './ObscurePassword'
import { generateRandomPassword } from './utils'

interface ResetPasswordFormValues {
    token: string
    password: string
    confirmPassword: string
}

interface ResetPasswordFormErrors extends FormikErrors<ResetPasswordFormValues> {
    format?: string
}

const ResetPassword = () => {
    const { t } = useTranslation()
    const { search } = useLocation()
    const history = useHistory()
    const passwordInputRef = useRef<HTMLInputElement>(null)

    const [showPasswordSuggestion, setShowPasswordSuggestion] = useState(false)
    const [suggestedPassword, setSuggestedPassword] = useState('')
    const [passwordFormatError, setPasswordFormatError] = useState(false)
    const [obscurePassword, setObscurePassword] = useState(true)

    const searchParams = queryString.parse(search)
    const token = typeof searchParams.token === 'string' ? searchParams.token : ''

    const passwordRegex = useMemo(() => PASSWORD_REGEX, [])

    const { mutate: resetPassword, isPending, isSuccess, error } = auth.ResetPassword.useMutation()

    const generatePasswordFormatError = ({ password }: { password: string }) => {
        const errors: ResetPasswordFormErrors = {}
        const isValidPassword = passwordRegex.test(password)

        if (!isValidPassword) {
            errors.format = t('form.register.error.passwordFormat')
        }

        return errors
    }

    const formik = useFormik<ResetPasswordFormValues>({
        enableReinitialize: true,
        initialValues: {
            token: '',
            password: '',
            confirmPassword: '',
        },
        validationSchema: Yup.object({
            password: Yup.string().required(t('app.common.password.error')),
            confirmPassword: Yup.string().oneOf([Yup.ref('password')], t('form.reset.error.confirmPassword')),
        }),

        validate: generatePasswordFormatError,
        onSubmit: (values) => {
            resetPassword({ ...values, token })
        },
    })

    useEffect(() => {
        if (!suggestedPassword) {
            const password = generateRandomPassword(passwordRegex) as string
            setSuggestedPassword(password)
        }

        setShowPasswordSuggestion(formik.values.password.length > 0 && formik.values.password.length <= 2)
    }, [formik.values.password, passwordRegex, suggestedPassword])

    useEffect(() => {
        if (isSuccess) {
            history.push('/login')
        }
    }, [history, isSuccess])

    const handleGenerateNewPassword = () => {
        setSuggestedPassword(generateRandomPassword(passwordRegex) as string)
    }

    const acceptPasswordSuggestion = () => {
        formik.setFieldValue('password', suggestedPassword)
        formik.setFieldValue('confirmPassword', suggestedPassword)
        setShowPasswordSuggestion(false)
    }

    useEffect(() => {
        const formikPassword = formik.values.password
        const isPasswordValid = passwordRegex.test(formikPassword)

        if (!isPasswordValid && formikPassword.length) {
            setPasswordFormatError(true)
        } else {
            setPasswordFormatError(false)
        }
    }, [formik.values.password, passwordRegex])

    if (!token) {
        history.push('/login')
        return null
    }

    return (
        <AuthLayout>
            <div className="auth-page-content">
                <Container>
                    <Row className="mt-4">
                        <Col lg={12}>
                            <div className="text-center mt-5 mb-5  text-white-50">
                                <div>
                                    <Link to="/" className="d-inline-block auth-logo">
                                        <img src={logoLight} alt="" />
                                    </Link>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row className="justify-content-center">
                        <Col md={8} lg={6} xl={5}>
                            <Card className="mt-5">
                                <CardBody className="p-4">
                                    <div className="text-center mt-2">
                                        <h5 className="text-primary fs-16">{t('form.resetPassword.title')}</h5>
                                    </div>
                                    <Alert className="border-0 alert-warning text-center mb-2 mx-2" role="alert">
                                        {t('form.resetPassword.message')}
                                    </Alert>
                                    <div className="p-2">
                                        {error ? (
                                            <Alert color="danger" style={{ marginTop: '13px' }}>
                                                {t('app.common.errorMessage')}
                                            </Alert>
                                        ) : null}

                                        {passwordFormatError ? (
                                            <Alert color="danger" className="text-center">
                                                {(formik.errors as ResetPasswordFormErrors).format}
                                            </Alert>
                                        ) : null}

                                        <Form
                                            onSubmit={(e) => {
                                                e.preventDefault()
                                                formik.handleSubmit()
                                                return false
                                            }}
                                            noValidate
                                        >
                                            <div className="mb-3">
                                                <Label htmlFor="password" className="form-label">
                                                    {t('app.common.password')}
                                                </Label>
                                                <div className="position-relative auth-pass-inputgroup mb-3">
                                                    <Input
                                                        id="password"
                                                        innerRef={passwordInputRef}
                                                        name="password"
                                                        type={obscurePassword ? 'password' : 'text'}
                                                        placeholder={t('app.common.password.placeholder')}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        value={formik.values.password || ''}
                                                        invalid={formik.touched.password && formik.errors.password ? true : false}
                                                    />

                                                    {showPasswordSuggestion ? (
                                                        <div className="form-control d-flex cursor-pointer">
                                                            <Button color="none" onClick={acceptPasswordSuggestion} className="w-50 m-0 p-0">
                                                                {suggestedPassword}
                                                            </Button>
                                                            <Button
                                                                color="none"
                                                                onClick={handleGenerateNewPassword}
                                                                className="w-50 m-0 p-0 text-muted"
                                                            >
                                                                {t('button.generateAgain')}
                                                            </Button>
                                                        </div>
                                                    ) : null}

                                                    {formik.touched.password && formik.errors.password ? (
                                                        <FormFeedback type="invalid" key={JSON.stringify(formik.errors.password)}>
                                                            <div>{formik.errors.password}</div>
                                                        </FormFeedback>
                                                    ) : null}

                                                    <ObscurePassword
                                                        errors={formik.errors}
                                                        obscurePassword={obscurePassword}
                                                        setObscurePassword={setObscurePassword}
                                                    />
                                                </div>
                                            </div>

                                            <div className="mb-3">
                                                <Label htmlFor="confirmPassword" className="form-label">
                                                    {t('form.register.field.confirmPassword')}
                                                </Label>
                                                <Input
                                                    id="confirmPassword"
                                                    name="confirmPassword"
                                                    type={obscurePassword ? 'password' : 'text'}
                                                    placeholder={t('form.register.placeholder.confirmPassword')}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.confirmPassword || ''}
                                                    invalid={formik.touched.confirmPassword && formik.errors.confirmPassword ? true : false}
                                                />
                                                {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
                                                    <FormFeedback type="invalid">
                                                        <div>{formik.errors.confirmPassword}</div>
                                                    </FormFeedback>
                                                ) : null}
                                            </div>

                                            <div className="text-center mt-4">
                                                <button className="btn btn-success w-100" type="submit">
                                                    {isPending ? <Spinner size="sm" /> : t('button.send')}
                                                </button>
                                            </div>
                                        </Form>
                                    </div>
                                </CardBody>
                            </Card>
                            <div className="mt-4 text-center">
                                <p className="mb-0">
                                    <Link to="/login" className="text-primary text-decoration-underline">
                                        {t('app.common.login')}
                                    </Link>
                                </p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </AuthLayout>
    )
}

export default ResetPassword
